import React, { useRef, useEffect } from 'react';

const DocumentUploader = ({ 
  documents, 
  handleFiles, 
  removeDocument, 
  isUploading
}) => {
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);
  
  // Setup drag and drop listeners
  useEffect(() => {
    const dropArea = dropAreaRef.current;
    
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    
    const highlight = () => {
      dropArea.classList.add('highlight');
    };
    
    const unhighlight = () => {
      dropArea.classList.remove('highlight');
    };
    
    const handleDrop = (e) => {
      preventDefaults(e);
      unhighlight();
      
      const files = e.dataTransfer.files;
      handleFiles(files);
    };
    
    if (dropArea) {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        dropArea.addEventListener(eventName, preventDefaults, false);
      });
      
      ['dragenter', 'dragover'].forEach(eventName => {
        dropArea.addEventListener(eventName, highlight, false);
      });
      
      ['dragleave', 'drop'].forEach(eventName => {
        dropArea.addEventListener(eventName, unhighlight, false);
      });
      
      dropArea.addEventListener('drop', handleDrop, false);
      
      // Cleanup
      return () => {
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
          dropArea.removeEventListener(eventName, preventDefaults);
        });
        
        ['dragenter', 'dragover'].forEach(eventName => {
          dropArea.removeEventListener(eventName, highlight);
        });
        
        ['dragleave', 'drop'].forEach(eventName => {
          dropArea.removeEventListener(eventName, unhighlight);
        });
        
        dropArea.removeEventListener('drop', handleDrop);
      };
    }
  }, [handleFiles]);
  
  const handleFileSelect = (e) => {
    handleFiles(e.target.files);
    // Reset file input
    e.target.value = null;
  };
  
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };
  
  return (
    <div className="document-section">
      <div 
        className="dropArea" 
        ref={dropAreaRef}
      >
        <p>Drag & drop documents here</p>
        <p>or</p>
        <button className="browse-button" onClick={handleBrowseClick}>
          Browse Files
        </button>
        <p className="file-types">Supported files: PDF, DOCX, DOC</p>
        <input
          aria-label="Upload documents"
          tabIndex="-1"
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          multiple
          accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
        />
      </div>
      
      {isUploading && (
        <div className="uploading">
          <p>Processing document(s)...</p>
        </div>
      )}
      
      {/* Processing information */}
      <div className="processing-info">
        <p>Documents will be automatically processed to fit within API limits</p>
      </div>
      
      {documents.length > 0 && (
        <div className="document-list">
          <h3>Uploaded Documents</h3>
          <ul>
            {documents.map(doc => (
              <li key={doc.id}>
                <span className="doc-name">{doc.name}</span>
                <button 
                  className="remove-doc" 
                  onClick={() => removeDocument(doc.id)}
                  aria-label={`Remove ${doc.name}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DocumentUploader;